import React, { useState, useEffect } from 'react';
import { Button, Typography, Space, Spin, message, Upload, Modal, Image } from 'antd';
import { DownloadOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { getToken } from '../tokens';

const { Text } = Typography;

interface FileInfo {
  id: string;
  filename_download: string;
  title: string;
  type: string;
  filesize: number | string;
  uploaded_on: string;
}

interface PhotoThumbnailProps {
  value?: string | { id?: string; [key: string]: any }; // JSON string or object containing file ID
  onChange?: (value: string | null) => void;
  disabled?: boolean;
}

const DIRECTUS_BASE_URL = 'https://melcarnedirectus2.digipulse.ch';

const PhotoThumbnail: React.FC<PhotoThumbnailProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [file, setFile] = useState<FileInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);

  // Parse file ID from JSON string or object
  const fileId = React.useMemo(() => {
    console.log("PhotoThumbnail value type:", typeof(value));
    console.log("PhotoThumbnail value:", value);
    
    if (!value) {
      console.log("PhotoThumbnail: No value provided");
      return null;
    }
    
    // If it's already an object with an id property, use that
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const objValue = value as { id?: string; filename_download?: string; type?: string; [key: string]: any };
      if (objValue.id) {
        console.log("PhotoThumbnail: Value is already an object with id:", objValue.id);
        return objValue.id;
      }
      // Check if it's a Directus file object with different structure
      if (objValue.filename_download || objValue.type) {
        console.log("PhotoThumbnail: Value appears to be a Directus file object, using id:", objValue.id);
        return objValue.id || null;
      }
    }
    
    // If it's a string, try to parse it as JSON
    if (typeof value === 'string') {
      // Check if it's already just an ID (UUID format)
      if (value.length === 36 && value.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        console.log("PhotoThumbnail: Value is a direct UUID:", value);
        return value;
      }
      
      try {
        const parsed = JSON.parse(value);
        console.log("PhotoThumbnail: Parsed JSON:", parsed);
        // If it's an object with an id property, use that
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.id) {
          return parsed.id;
        }
        // If it's a string, use it directly
        if (typeof parsed === 'string') {
          return parsed;
        }
        console.log("PhotoThumbnail: Parsed value doesn't contain id");
        return null;
      } catch (e) {
        console.log("PhotoThumbnail: JSON parse error:", e);
        // If parsing fails and it's not empty, might be a direct ID
        if (value.length > 0) {
          console.log("PhotoThumbnail: Using value as direct ID:", value);
          return value;
        }
        return null;
      }
    }
    
    console.log("PhotoThumbnail: Could not extract file ID from value");
    return null;
  }, [value]);
  
  console.log("PhotoThumbnail: Final fileId:", fileId);

  // Fetch file information and load image as blob
  useEffect(() => {
    const fetchFile = async () => {
      if (!fileId) {
        setFile(null);
        setImageBlobUrl(null);
        return;
      }

      setLoading(true);
      try {
        const { token } = getToken();
        
        // Fetch file metadata
        const fileResponse = await fetch(`${DIRECTUS_BASE_URL}/files/${fileId}`, {
          cache: 'no-store',
          headers: {
            'Authorization': `Bearer ${token || ''}`
          }
        });
        
        if (fileResponse.ok) {
          const result = await fileResponse.json();
          setFile(result.data);
          
          // Fetch image as blob with authentication
          const imageResponse = await fetch(`${DIRECTUS_BASE_URL}/assets/${fileId}`, {
            cache: 'no-store',
            headers: {
              'Authorization': `Bearer ${token || ''}`
            }
          });
          
          if (imageResponse.ok) {
            const blob = await imageResponse.blob();
            const blobUrl = URL.createObjectURL(blob);
            setImageBlobUrl(blobUrl);
          } else {
            console.error('Error fetching image:', imageResponse.status);
            setImageBlobUrl(null);
          }
        } else {
          setFile(null);
          setImageBlobUrl(null);
        }
      } catch (error) {
        console.error('Error fetching file:', error);
        messageApi.error('Erreur lors du chargement de la photo');
        setFile(null);
        setImageBlobUrl(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
    
    // Cleanup blob URL on unmount
    return () => {
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
    };
  }, [fileId, messageApi]);

  // Handle file download
  const handleDownload = () => {
    if (!file) return;
    const { token } = getToken();
    const downloadUrl = `${DIRECTUS_BASE_URL}/assets/${file.id}?download${token ? `&access_token=${token}` : ''}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = file.filename_download || file.title || `photo-${file.id}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file preview/view
  const handleView = () => {
    if (!file) return;
    setPreviewVisible(true);
  };

  // Handle file removal
  const handleRemove = () => {
    Modal.confirm({
      title: 'Supprimer la photo',
      content: 'Êtes-vous sûr de vouloir supprimer cette photo?',
      okText: 'Supprimer',
      cancelText: 'Annuler',
      okType: 'danger',
      onOk: () => {
        onChange?.(null);
        setFile(null);
        setImageBlobUrl(null);
        messageApi.success('Photo supprimée');
      }
    });
  };

  // Handle file upload
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${DIRECTUS_BASE_URL}/files`,
    headers: {
      Authorization: `Bearer ${getToken().token || ''}`
    },
    accept: 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif',
    showUploadList: false,
    customRequest: async (options) => {
      const { file, onSuccess, onError } = options;
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch(`${DIRECTUS_BASE_URL}/files`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken().token || ''}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          onSuccess?.(result);
        } else {
          throw new Error(`Upload failed: ${response.status}`);
        }
      } catch (error) {
        onError?.(error as Error);
      }
    },
    beforeUpload: (file) => {
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      
      // Check file type - only images
      const allowedTypes = [
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'image/webp',
        'image/heic',
        'image/heif'
      ];
      
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
      
      const isValidType = allowedTypes.includes(file.type) && allowedExtensions.includes(fileExtension || '');
      
      if (!isValidType) {
        messageApi.error(`Le fichier "${file.name}" n'est pas une image valide. Formats acceptés: JPG, PNG, GIF, WEBP, HEIC`);
        return false;
      }
      
      if (file.size > maxSize) {
        messageApi.error(`Le fichier "${file.name}" est trop volumineux. Taille maximale autorisée: 10MB`);
        return false;
      }
      
      setUploading(true);
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        const newFileId = info.file.response?.data?.id;
        if (newFileId) {
          // Store as JSON object with id property
          const jsonValue = JSON.stringify({ id: newFileId });
          onChange?.(jsonValue);
          messageApi.success(`${info.file.name} téléchargé avec succès`);
        }
        setUploading(false);
      } else if (info.file.status === 'error') {
        messageApi.error(`Erreur lors du téléchargement de ${info.file.name}`);
        setUploading(false);
      }
    },
    onRemove: () => {
      setUploading(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Use blob URL if available, otherwise fallback to direct URL
  const imageUrl = imageBlobUrl || (file ? `${DIRECTUS_BASE_URL}/assets/${file.id}` : null);

  return (
    <div>
      {contextHolder}
      
      {/* Upload section */}
      {!disabled && !file && (
        <Space style={{ marginBottom: 16 }}>
          <Upload {...uploadProps}>
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? 'Téléchargement...' : 'Télécharger une photo'}
            </Button>
          </Upload>
        </Space>
      )}

      {/* Photo display */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin size="large" />
          <div>Chargement de la photo...</div>
        </div>
      ) : file && imageUrl ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Thumbnail preview */}
          <div style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: 8, 
            padding: 8,
            backgroundColor: '#fafafa',
            display: 'inline-block',
            width: 'fit-content'
          }}>
            <Image
              src={imageUrl}
              alt={file.filename_download || file.title}
              width={150}
              height={150}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              preview={{
                visible: previewVisible,
                onVisibleChange: setPreviewVisible,
              }}
            />
          </div>

          {/* File info */}
          <div style={{ 
            padding: 12, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 6 
          }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>{file.filename_download || file.title}</Text>
              <Text type="secondary">
                Taille: {formatFileSize(typeof file.filesize === 'number' ? file.filesize : parseInt(file.filesize) || 0)}
              </Text>
              <Text type="secondary">
                Type: {file.type}
              </Text>
            </Space>
          </div>

          {/* Actions */}
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={handleView}
            >
              Voir
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Télécharger
            </Button>
            {!disabled && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemove}
              >
                Supprimer
              </Button>
            )}
            {!disabled && (
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? 'Téléchargement...' : 'Remplacer'}
                </Button>
              </Upload>
            )}
          </Space>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
          {disabled ? 'Aucune photo' : 'Aucune photo. Utilisez le bouton ci-dessus pour télécharger une photo.'}
        </div>
      )}
    </div>
  );
};

export default PhotoThumbnail;
